import * as React from 'react';
import * as autoBind from 'react-autobind';
import * as classNames from 'classnames';
import ReviewService from '../../services/ReviewService/ReviewService';
import TimeFormatter from '../../utils/TimeFormatter/TimeFormatter';
import UserMessaging from '../../utils/UserMessaging';
import Strings from '../../globals/Strings';
import CommentModel from '../../types/CommentModel';
import IVideoPlayer from '../../types/IVideoPlayer';
import Constants from '../../globals/Constants';
import CircleCheckBox from '../CircleCheckBox/CircleCheckBox';
import BasicCommentContainer from '../BasicComment/BasicCommentContainer';
import CommentRepliesContainer from '../CommentReplies/CommentRepliesContainer';
import FreehandGlyph from '../../assets/FreehandGlyph.svg';
import {AnnotationModes} from '../../globals/Constants';
import {Glyphs, ControlButton, TscButtonType, TscButtonGlyphPlacement} from '@techsmith/tsc-cloud-style-guide';
import CommentScrollingService from '../../utils/CommentScroller';
import GoogleAnalyticsService from '../../services/GoogleAnalyticsService/GoogleAnalyticsService';
import Themeable from '../../types/Themeable';
import AriaLiveStatusHelper from '../../utils/AriaLiveStatusHelper';
import {RouteComponentProps} from 'react-router';

export default class ParentComment extends React.Component<ParentCommentProps, ParentCommentState> {
   readonly state: ParentCommentState = {saving: false, likeInProgress: false, isResolveInProgress: false, deleteAnnotationOnSave: false, isExpanded: !!this.childIsSelected(this.props), feedbackHasFocus: false};
   commentRef: HTMLElement;

   constructor(props: ParentCommentProps) {
      super(props);
      autoBind(this);
   }

   componentDidUpdate(prevProps: ParentCommentProps, prevState: ParentCommentState) {
      const {isSelected} = this.props;

      if (!prevProps.isSelected && isSelected) {
         this.scrollIntoView();
      }

      if (!this.childIsSelected(prevProps) && this.childIsSelected(this.props)) {
         this.setState({isExpanded: true});
         this.scrollIntoView();
      }
   }

   componentDidMount() {
      if (this.props.isSelected || this.childIsSelected(this.props)) {
         this.scrollIntoView();
      }
   }

   scrollIntoView() {
      CommentScrollingService.scrollCommentIntoView(this.commentRef, {scrollMode: window.innerWidth >= Constants.mobileWidthBreakpoint ? 'if-needed' : 'always'});
   }

   childIsSelected(props: ParentCommentProps) {
      const {selectedComment, replies} = props;

      return selectedComment && replies && !!replies.find(r => r.id === selectedComment.id);
   }

   deleteAnnotation() {
      this.setState({deleteAnnotationOnSave: true});
      this.props.setHideAnnotation(true);
   }

   onAnnotationGlyphClick(e: any): void {
      this.closeEditIfOpen();

      if (this.state.deleteAnnotationOnSave) {
         if (this.props.isSelected) {
            this.setState({deleteAnnotationOnSave: false});
            this.props.setHideAnnotation(false);
         }
      } else {
         this.props.setHideAnnotation(!this.props.hideAnnotation);
      }

      this.selectAndPause();
      this.props.setAnnotationMode(AnnotationModes.none);

      e.stopPropagation(); // Stops the outer comment click from triggering
   }

   onReplyClick(): void {
      this.closeEditIfOpen();
      this.selectAndPause();
      this.props.setAnnotationMode(AnnotationModes.none);
      this.setState({isExpanded: true, feedbackHasFocus: true});
   }

   closeEditIfOpen(): void {
      if (this.props.isAnyEditing && !this.props.isEditing) {
         this.props.onEditing();
      }
   }

   onCommentClick(): void {
      this.selectAndPause();
      this.closeEditIfOpen();
   }

   onCommentTimeClick(): void {
      const {comment} = this.props;
      AriaLiveStatusHelper.readAlert(Strings.accessibility.comments.commentTimeButtonAriaLiveText.replace('{time}', TimeFormatter.formatTimeForSpeech(comment.time)));
      this.onCommentClick();
   }

   onEditClick() {
      this.selectAndPause();
   }

   selectAndPause() {
      const {player, onSelect, comment, isSelected, isQuizzing} = this.props;

      const timeDifference = TimeFormatter.getRoundedTime(Math.abs(player.getCurrentTime() - comment.time));
      const timeMismatch = timeDifference > Constants.annotation.timeTolerance && !player.getIsNullInstance();

      if ((!isSelected && !this.childIsSelected(this.props)) || timeMismatch) {
         onSelect(comment.id);

         if (timeMismatch) {
            player.pause();
            !isQuizzing && player.setCurrentTime(comment.time);
         }
      }
   }

   onToggleResolved(e: any): Promise<void> {
      e.stopPropagation();
      e.preventDefault();

      if (!this.props.isOwner) {
         return;
      }

      this.setState({isResolveInProgress: true});

      if (!this.props.comment.isResolved) {
         return ReviewService.resolveComment(this.props.comment).then(() => {
            GoogleAnalyticsService.trackCommenting(this.props.currentUserId, Constants.googleAnalytics.commenting.actions.resolveComment);
            this.setState({isResolveInProgress: false});
         }).catch(() => {
            UserMessaging.warn(Strings.errors.failedSaveComment);
            this.setState({isResolveInProgress: false});
         });
      } else {
         return ReviewService.unresolveComment(this.props.comment).then(() => {
            this.setState({isResolveInProgress: false});
         }).catch(() => {
            UserMessaging.warn(Strings.errors.failedSaveComment);
            this.setState({isResolveInProgress: false});
         });
      }
   }

   newReply(text: string): any {
      return ReviewService.addReply({
         annotation: '',
         text: text
      }, this.props.comment).catch(() => {
         UserMessaging.warn(Strings.errors.failedSaveComment);
         return Promise.reject(Strings.errors.failedSaveComment);
      });
   }

   onSaveEdit() {
      this.setState({deleteAnnotationOnSave: false});
   }

   onCancelEdit() {
      this.setState({deleteAnnotationOnSave: false});
      this.props.setHideAnnotation(false);
   }

   handleFeedbackBlur() {
      this.setState({feedbackHasFocus: false});
   }

   render() {
      const {comment, showCommentTime, isSelected, replies, isEditing, annotationMode, hideAnnotation, isOwner, shouldShowUpdate, themeName} = this.props;
      const {isResolveInProgress, deleteAnnotationOnSave, isExpanded, feedbackHasFocus} = this.state;

      const isSelectedOrChildIsSelected = isSelected || this.childIsSelected(this.props);
      const commentThreadClasses = classNames('comment-thread', {selected: isSelectedOrChildIsSelected, highlighted: shouldShowUpdate});
      const alternateEditStateInformation = comment.isResolved ? `${Strings.labels.resolved} ${TimeFormatter.formatDate(comment.resolutionDate)}` : '';
      const annotationClasses = classNames('clear-button-default-stlying show-annotation-glyph comment-status-control comment-status-badge', {selected: isSelectedOrChildIsSelected && annotationMode === AnnotationModes.display && !hideAnnotation}, {pendingDeletion: deleteAnnotationOnSave});
      const commentResolutionClassList = classNames('comment-resolution comment-status-control', isResolveInProgress ? 'active' : 'inactive');
      const timestampClasses = classNames('clear-button-default-stlying comment-status-control time comment-status-badge', {selected: isSelectedOrChildIsSelected});
      const annotationAriaLabel = Strings.accessibility.comments.anotationsAriaLabel;

      let expandThreadText: string = isExpanded ? Strings.labels.commentReplyThread.hide : Strings.labels.commentReplyThread.show;
      expandThreadText = `${expandThreadText} ${replies.length} ${replies.length > 1 ? Strings.labels.commentReplyThread.replies : Strings.labels.commentReplyThread.reply}`;

      return (
         <div className={commentThreadClasses} ref={c => this.commentRef = c}>
            <BasicCommentContainer
               comment={comment}
               isEditable={!comment.isResolved}
               alternateDisplayText={alternateEditStateInformation}
               deleteAnnotationOnSave={deleteAnnotationOnSave}
               onSaveEdit={this.onSaveEdit}
               onCancelEdit={this.onCancelEdit}
               onSelect={this.onCommentClick}
               onReplyClick={this.onReplyClick}
               onEditClick={this.onEditClick}
            >
               {comment.annotation && (
                  <button className={annotationClasses} onClick={this.onAnnotationGlyphClick} aria-label={annotationAriaLabel} title={hideAnnotation ? Strings.labels.annotations.showAnnotation : Strings.labels.annotations.hideAnnotation}>
                     <FreehandGlyph />
                     {comment.annotation && comment.text && isEditing && !deleteAnnotationOnSave && <button className="remove-annotation-button" onClick={this.deleteAnnotation} title={Strings.labels.annotations.removeAnnotation}><Glyphs.Close16x16 className="remove-annotation-glyph" /></button>}
                  </button>
               )}
               {showCommentTime && <button className={timestampClasses} onClick={this.onCommentTimeClick} aria-label={TimeFormatter.formatTimeForSpeech(comment.time)}>{TimeFormatter.formatTime(comment.time)}</button>}
               {!isEditing && (isOwner || comment.isResolved) && <div className={commentResolutionClassList}>
                  <CircleCheckBox isSelected={comment.isResolved} isActive={isResolveInProgress} isOwner={isOwner} onClick={this.onToggleResolved} ariaLabel={Strings.accessibility.comments.resolveCommentAriaLabel} />
               </div>}
            </BasicCommentContainer>
            {!!replies.length &&
               <div className="expand-replies-button">
                  <ControlButton buttonType={TscButtonType.floating}
                     onClick={() => this.setState({isExpanded: !isExpanded})}
                     label={expandThreadText}
                     glyph={isExpanded ? <Glyphs.ArrowUp16x16 /> : <Glyphs.ArrowDown16x16 />}
                     glyphPlacement={TscButtonGlyphPlacement.left}
                     themeName={themeName} />
               </div>}
            {isExpanded && <CommentRepliesContainer parentComment={comment}
               commentReplies={replies}
               isEditable={true}
               addReply={this.newReply}
               hasFocus={feedbackHasFocus}
               onBlur={this.handleFeedbackBlur}
               isSelected={isSelected}
               onSelect={this.selectAndPause} />}
         </div>
      );
   }
}

export interface ParentCommentProps extends RouteComponentProps, Themeable {
   comment: CommentModel;
   showCommentTime: boolean;
   isSelected: boolean;
   isEditing: boolean;
   isAnyEditing: boolean;
   player: IVideoPlayer;
   replies: CommentModel[];
   annotationMode: AnnotationModes;
   hideAnnotation: boolean;
   selectedComment: CommentModel;
   isOwner: boolean;
   shouldShowUpdate: boolean;
   currentUserId: string;
   isQuizzing: boolean;
   onSelect(commentId: string): void;
   onEditing(commentId?: string): void;
   setHideAnnotation(isHidden: boolean): void;
   setAnnotationMode(annotationMode: AnnotationModes): void;
}

export interface ParentCommentState {
   saving: boolean;
   likeInProgress: boolean;
   isResolveInProgress: boolean;
   deleteAnnotationOnSave: boolean;
   isExpanded: boolean;
   feedbackHasFocus: boolean;
}
